import { useEffect, useMemo, useRef, type ReactNode } from "react";
import type { MessageType } from "../types/chat.types";
import { groupMessagesByDate } from "../utils/formatMessage";
import { autoScrollToBottom } from "../utils/autoScroll";
import { MessageItem } from "./MessageItem";

interface MessageListProps {
  messages: MessageType[];
  footer?: ReactNode;
}

export const MessageList = ({ messages, footer }: MessageListProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const grouped = useMemo(
    () => groupMessagesByDate(messages),
    [messages],
  );

  useEffect(() => {
    autoScrollToBottom(containerRef.current);
  }, [messages.length, footer]);

  return (
    <div
      ref={containerRef}
      className="flex-1 space-y-6 overflow-y-auto px-6 py-6"
      aria-live="polite"
      aria-label="Conversation messages"
    >
      {grouped.length === 0 && (
        <div className="flex h-full flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50/70 p-8 text-center">
          <p className="text-base font-semibold text-slate-600">
            You haven’t started chatting yet
          </p>
          <p className="mt-2 max-w-sm text-sm text-slate-500">
            Say hello and we’ll keep everything synced in real time using
            Twilio Conversations.
          </p>
        </div>
      )}

      {grouped.map((group) => (
        <div key={group.dateLabel} className="space-y-3">
          <div className="text-center text-xs font-semibold uppercase tracking-widest text-slate-400">
            {group.dateLabel}
          </div>
          <div className="flex flex-col gap-4">
            {group.items.map((item) => (
              <MessageItem key={item.sid} message={item} />
            ))}
          </div>
        </div>
      ))}

      {footer && <div className="pt-2">{footer}</div>}
    </div>
  );
};


